import { Button, Table, Modal } from "@telluscompany/design-system";
import { useSearchParams } from "react-router-dom";
import { PlusCircle } from "lucide-react";
import { DeleteModal } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";
import { useCrudModals } from "@shared/hooks";
import { Gs3TablePaginator } from "@shared/components";
import { EMPTY_PAGINATION_META } from "@shared/constants/pagination.constants";
import type {
  ListStructuresResponse,
  StructureFilters,
} from "../../api/structures/structures.types";
import type { StructureFormData } from "../../schemas";
import type { EditStructureFormData } from "../../schemas";
import {
  useCreateStructureMutation,
  useUpdateStructureMutation,
  useDeleteStructureMutation,
} from "../../api/structures/structures.mutations";
import { CreateStructureForm } from "./CreateStructureForm";
import { EditStructureForm } from "./EditStructureForm";
import { StructuresFilter } from "./StructuresFilter";
import { StructuresActionMenu } from "./StructuresActionMenu";
import { PaginationMeta } from "@shared/api";
import { useUpdateTableUserPreferences } from "@shared/hooks";
import { useUserContext } from "@/shared/contexts";

interface StructuresTabProps {
  tenantId: string;
  structures: ListStructuresResponse[];
  isLoading?: boolean;
  onFilter: (filters: Partial<StructureFilters>) => void;
  onClearFilter: () => void;
  initialFilters?: Partial<StructureFilters>;
  paginatedMeta?: PaginationMeta;
}

export const StructuresTab = ({
  tenantId,
  structures,
  onFilter,
  onClearFilter,
  initialFilters,
  paginatedMeta,
}: StructuresTabProps) => {
  const { translate } = useLocalization();

  const {
    modals,
    selectedItem: selectedStructure,
    openCreate,
    openEdit,
    openDelete,
    closeCreate,
    closeEdit,
    closeDelete,
  } = useCrudModals<ListStructuresResponse>();

  const selectedStructureId = selectedStructure?.id ?? "";
  const createMutation = useCreateStructureMutation(tenantId);
  const updateMutation = useUpdateStructureMutation(
    tenantId,
    selectedStructureId,
  );
  const deleteMutation = useDeleteStructureMutation(tenantId);

  const { user } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(user?.userPreferences);

  const handleCreateStructure = (data: StructureFormData) => {
    createMutation.mutate(
      {
        complexId: data.complexId,
        name: data.name,
      },
      {
        onSuccess: () => closeCreate(),
      },
    );
  };

  const handleEditStructure = (data: EditStructureFormData) => {
    updateMutation.mutate(
      { name: data.name },
      {
        onSuccess: () => closeEdit(),
      },
    );
  };

  const handleDeleteStructure = () => {
    if (selectedStructure?.id) {
      deleteMutation.mutate(selectedStructure.id, {
        onSuccess: () => closeDelete(),
      });
    }
  };

  const createActionMenu = (rowData: { rowData: ListStructuresResponse }) => {
    const structure = rowData.rowData;

    return (
      <StructuresActionMenu
        structure={structure}
        onEdit={() => openEdit(structure)}
        onDelete={() => openDelete(structure)}
      />
    );
  };

  const tableData =
    (structures as unknown as Record<string, unknown>[]) ?? [];

  const tableColumns = [
    {
      key: "name",
      label: translate("management.structures.table.column.name"),
      sortType: "text" as const,
      minWidth: "150px",
    },
    {
      key: "complexName",
      label: translate("management.structures.table.column.complexName"),
      sortType: "text" as const,
      minWidth: "150px",
    },
  ];

  return (
    <div className="p-4">
      <Table<ListStructuresResponse>
        userId="STRUCTURE1"
        usersPreferencesFromServer={user?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        useSearchParamsHook={useSearchParams}
        enableSortByUrl
        TableActionMenu={ user?.role === "owner" ? createActionMenu as React.ComponentType : undefined}
        TableButtons={
          user?.role === "owner" && (
          <Button
            size="small"
            variant="primary"
            onClick={openCreate}
            iconLeft={<PlusCircle />}
          >
            {translate("management.structures.button.new")}
          </Button>
          )
        }
        TableFilter={() => (
          <StructuresFilter
            onFilter={onFilter}
            onClear={onClearFilter}
            initialFilters={initialFilters}
          />
        )}
        TablePaginator={
          <Gs3TablePaginator
            meta={paginatedMeta ?? EMPTY_PAGINATION_META}
            useUrlState
            useSearchParamsHook={useSearchParams}
          />
        }
        draggable
        enableTableColumnsSelector
        tableColumns={tableColumns}
        tableData={tableData}
        tableId="structures-table"
        filterButtonText={translate(
          "management.structures.table.filterButtonText",
        )}
      />

      {/* Modal de Criação */}
      <Modal
        open={modals.create}
        onClose={closeCreate}
        title={translate("management.structures.modal.title.create")}
        content={
          <CreateStructureForm
            tenantId={tenantId}
            onSubmit={handleCreateStructure}
            onCancel={closeCreate}
            isSubmitting={createMutation.isPending}
          />
        }
      />

      {/* Modal de Edição */}
      <Modal
        open={modals.edit}
        onClose={closeEdit}
        title={translate("management.structures.modal.title.edit")}
        content={
          <EditStructureForm
            tenantId={tenantId}
            structureId={selectedStructureId}
            onSubmit={handleEditStructure}
            onCancel={closeEdit}
            isSubmitting={updateMutation.isPending}
          />
        }
      />

      {/* Modal de Exclusão */}
      <DeleteModal
        open={modals.delete}
        onClose={closeDelete}
        onConfirm={handleDeleteStructure}
        isDeleting={deleteMutation.isPending}
        itemDescription={selectedStructure?.name}
        title={translate("common.deleteModalTitle")}
        message={translate("common.deleteModalItemMessage")}
        alertMessage={translate("common.deleteModalAlertMessage")}
        confirmButtonText={translate("common.delete")}
        cancelButtonText={translate("common.cancel")}
        deletingButtonText={translate("common.deleting")}
      />
    </div>
  );
};

export default StructuresTab;
